/**
 * Performance monitoring utilities
 */

// Performance measurement wrapper
export const measurePerformance = (name, fn) => {
  return (...args) => {
    const start = performance.now();
    const result = fn(...args);
    const end = performance.now();
    
    if (import.meta.env.DEV) {
      console.log(`Performance: ${name} took ${end - start} milliseconds`);
    }
    
    return result;
  };
};

// Bundle size monitoring
export const logBundleInfo = () => {
  if (import.meta.env.DEV && 'performance' in window) {
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0];
      console.log('Bundle Performance:', {
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
        totalSize: navigation.transferSize,
      });
    });
  }
};

// Memory usage monitoring
export const monitorMemoryUsage = () => {
  if (import.meta.env.DEV && 'memory' in performance) {
    setInterval(() => {
      const memory = performance.memory;
      console.log('Memory Usage:', {
        used: Math.round(memory.usedJSHeapSize / 1048576) + ' MB',
        total: Math.round(memory.totalJSHeapSize / 1048576) + ' MB',
        limit: Math.round(memory.jsHeapSizeLimit / 1048576) + ' MB',
      });
    }, 30000); // Log every 30 seconds
  }
};

// Component render tracking
export const withRenderTracking = (Component, componentName) => {
  if (import.meta.env.PROD) return Component;
  
  return (props) => {
    if (typeof window !== 'undefined' && 'performance' in window) {
      const renderStart = performance.now();
      
      // Use setTimeout to measure after render
      setTimeout(() => {
        const renderEnd = performance.now();
        console.log(`Render: ${componentName} took ${renderEnd - renderStart} milliseconds`);
      }, 0);
    }
    
    return Component(props);
  };
};

// Debounce utility for performance optimization
export const debounce = (func, wait, immediate = false) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

// Throttle utility for performance optimization
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Image lazy loading intersection observer
export const createImageObserver = (callback) => {
  if (!('IntersectionObserver' in window)) {
    // Fallback for browsers without IntersectionObserver
    return {
      observe: () => callback(),
      disconnect: () => {},
    };
  }
  
  return new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback(entry.target);
        }
      });
    },
    {
      rootMargin: '50px',
      threshold: 0.1,
    }
  );
};